<template lang="pug">
v-card
  v-card-title
    span Simulador de precio
    ShowInfoBtn(@click="$refs.infoAlert.showAlert()")
  v-card-text
    InfoAlert(:message="alertMessage" ref="infoAlert")
    p.text--secondary.mb-1 ¿Qué pasa con tu margen si ajustas tus precios? Mueve el control.
    v-slider.mt-6(
      v-model="pct"
      min="-20" max="20" step="1"
      thumb-label="always"
      :thumb-size="30"
      color="primary"
      track-color="grey lighten-2")
      template(v-slot:thumb-label="{ value }") {{ value > 0 ? '+' : '' }}{{ value }}%
      template(v-slot:prepend)
        span.text--secondary −20%
      template(v-slot:append)
        span.text--secondary +20%
    v-row.mt-2
      v-col(cols="12" md="4")
        .metric
          .metric-label Venta anual
          .metric-value {{ newSales | moneyMM }}
          .metric-delta(:class="deltaClass(newSales - baseSales)") {{ deltaMoney(newSales - baseSales) }}
      v-col(cols="12" md="4")
        .metric
          .metric-label Margen bruto
          .metric-value {{ newMargin | moneyMM }}
          .metric-delta(:class="deltaClass(newMargin - baseMargin)") {{ deltaMoney(newMargin - baseMargin) }}
      v-col(cols="12" md="4")
        .metric
          .metric-label Margen sobre venta
          .metric-value {{ newMarginPct.toFixed(1) }}%
          .metric-delta(:class="deltaClass(newMarginPct - baseMarginPct)") {{ (newMarginPct - baseMarginPct >= 0 ? '+' : '') }}{{ (newMarginPct - baseMarginPct).toFixed(1) }} pts
    p.caveat.mt-3.mb-0 Simulación asumiendo el mismo volumen de venta. Un alza de precio real puede reducir la cantidad que te compran.
</template>

<script>
import { mapActions } from 'vuex'
import { fmtMoneyMM } from '@/utils/customVueFilters'
import InfoAlert from '../../common/InfoAlert.vue'
import ShowInfoBtn from '../../common/ShowInfoBtn.vue'

export default {
  props: {
    filters: {
      type: Object,
      required: true,
      default: () => ({})
    },
    projection: {
      type: Number,
      default: null,
    },
  },
  components: {
    InfoAlert,
    ShowInfoBtn,
  },
  data() {
    return {
      pct: 0,
      baseSales: 0,
      baseMargin: 0,
      alertMessage: `
<p>
Este simulador muestra el <b>efecto de cambiar tus precios</b> sobre tu venta y tu margen, manteniendo el mismo volumen. Como los costos no cambian, cada peso de aumento de precio se convierte casi entero en margen: por eso el precio es una de las palancas más potentes de tu rentabilidad. Úsalo para explorar cuánto espacio tienes para subir (o cuánto te cuesta un descuento).
</p>`,
    }
  },
  computed: {
    baseMarginPct() {
      return this.baseSales ? (this.baseMargin / this.baseSales) * 100 : 0
    },
    newSales() {
      return this.baseSales * (1 + this.pct / 100)
    },
    newMargin() {
      // costo constante: el margen sube (o baja) en el mismo monto que cambia la venta
      return this.baseMargin + (this.pct / 100) * this.baseSales
    },
    newMarginPct() {
      return this.newSales ? (this.newMargin / this.newSales) * 100 : 0
    },
  },
  methods: {
    ...mapActions('commercial_projection', ['getOptimalMixStatistics']),
    deltaMoney(v) {
      if (!v) return 'Sin cambio'
      const sign = v > 0 ? '+' : '−'
      return sign + fmtMoneyMM(Math.abs(v))
    },
    deltaClass(v) {
      if (!v) return 'delta--flat'
      return v > 0 ? 'delta--up' : 'delta--down'
    },
    async fetchData() {
      const params = {
        ...this.filters,
        projection: this.projection || this.$route.params.id,
      }
      const { data, error } = await this.getOptimalMixStatistics(params)
      if (!error && data) {
        this.baseSales = data.total_sales || 0
        this.baseMargin = data.total_profit || 0
      }
    },
  },
  watch: {
    filters: {
      handler() { this.fetchData() },
      deep: true,
    },
  },
  created() {
    this.fetchData()
  },
}
</script>

<style scoped>
.metric { border: 1px solid #ECEDF0; border-radius: 12px; padding: 14px 16px; height: 100%; }
.metric-label { font-size: 12px; color: #8A99A3; }
.metric-value { font-size: 22px; font-weight: 700; color: #1B2E38; font-variant-numeric: tabular-nums; margin-top: 2px; }
.metric-delta { font-size: 13px; font-weight: 600; margin-top: 4px; font-variant-numeric: tabular-nums; }
.delta--up { color: #0FA890; }
.delta--down { color: #E0533C; }
.delta--flat { color: #B9C4CC; }
.caveat { font-size: 12px; color: #8A99A3; font-style: italic; }
</style>
